/* Copyright (c) 2021-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Draggable } from "react-beautiful-dnd";
import styled from "styled-components";
import { FlexContainer, SVGIcon, Tag, theme } from "@nordcloud/gnui";
import { ColumnType, Item, SelectionMethod } from "~/types";
import { OSCell } from "~/views/resources/components/OSCell";
import { ResourceDelete } from "../Resource/components/ResourceDelete";
import { IconColumn } from "../styles";
import { NameWithId } from "../Table";

type Props<T> = {
  items: T[];
  removeItem: (item: T) => void;
};

export function ComponentDraggableList<T extends Item>({
  items,
  removeItem,
}: Props<T>) {
  return (
    <FlexContainer as="ul" direction="column" alignItems="stretch">
      {items?.map((item, index) => {
        const itemOs = item.columns?.find((c) => c.type === ColumnType.os);
        const os = {
          subType: itemOs?.data[0] ?? "",
          type: itemOs?.data[1] ?? "",
        };
        const selectionMethod = item.columns?.find(
          (c) => c.type === ColumnType.selectionMethod
        );

        if (selectionMethod?.data[0] === SelectionMethod.manual) {
          return (
            <Draggable
              key={`${item.id}-manual`}
              draggableId={`${item.id}-manual`}
              index={index}
            >
              {(provided) => (
                <ListItem
                  aria-label={item.name}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                >
                  <IconColumn {...provided.dragHandleProps}>
                    <SVGIcon
                      color={theme.color.text.text01}
                      name="dragAndDrop"
                    />
                  </IconColumn>
                  <IconColumn>
                    <OSCell os={os} />
                  </IconColumn>
                  <TextColumn>
                    <NameWithId resourceId={item.id} resourceName={item.name} />
                  </TextColumn>
                  <Tag
                    text={SelectionMethod.manual}
                    color="blue"
                    aria-label="manual method"
                  />
                  <ResourceDelete
                    resourceName={item.name ?? ""}
                    handleRemove={() => removeItem(item)}
                  />
                </ListItem>
              )}
            </Draggable>
          );
        }
        return (
          <ListItem key={`${item.id}-dynamic`} aria-label={item.name}>
            <IconColumn />
            <IconColumn>
              <OSCell os={os} />
            </IconColumn>
            <TextColumn>
              <NameWithId resourceId={item.id} resourceName={item.name} />
            </TextColumn>
            <Tag
              text={SelectionMethod.dynamic}
              color="purple"
              aria-label="dynamic method"
            />
          </ListItem>
        );
      })}
    </FlexContainer>
  );
}

const ListItem = styled.li`
  padding: ${theme.spacing.spacing01} ${theme.spacing.spacing02};
  display: flex;
  align-items: center;
  border: 1px solid #e7e7eb;
  border-radius: ${theme.radius.md};
  background-color: ${theme.color.background.ui01};
  margin: ${theme.spacing.spacing01} 0;
  word-break: break-word;
  &:hover {
    border: 1px solid var(--border-focus);
  }
`;

const TextColumn = styled.div`
  padding: ${theme.spacing.spacing01} ${theme.spacing.spacing02};
  flex: 2 1 0;
`;
